import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useXp } from '../../../context/XpContext';
import { attendusJouables, defisCreditesAffiches, defisTotalAffiche, defisEpuises } from '../../../utils/defisQuotidiens';
import { DEFIS_QUOTA_JOUR } from '../../../utils/xpConfig';
import { sonClicVolcan, sonPlongee, sonPierre, sonEruption } from '../../../utils/sonsVolcan';
import { lorePortail } from '../../../utils/ileLore';
import { VOLCAN_EXT } from './decorRegions';
import FicheDefi from './FicheDefi';

/**
 * « Le Volcan des Nombres » — expérience immersive de la région maths
 * (design utilisateur, porté du mockup HTML). Deux scènes :
 *  1. EXTÉRIEUR : volcan de nuit, ciel étoilé, CTA « Plonger dans le
 *     cratère » → transition dive (zoom + flash) ;
 *  2. CRATÈRE : braises (canvas), noyau pulsant entouré de 5 PIERRES
 *     (= l'énergie du volcan), PORTAILS en polygone = les THÈMES réels.
 *
 * LE QUOTA DEVIENT L'OBJECTIF : énergie = defis_credites_jour (back).
 * Chaque défi crédité allume une pierre ; à 5/5 → ÉRUPTION (une fois par
 * jour). Au-delà : portails jouables « sans XP », à 30 → repos.
 *
 * Un défi = NOTRE moteur (FicheDefi → onStartDefi → Quiz plein écran).
 * Lancer un défi démonte l'île : on persiste région + compteur en
 * sessionStorage pour ROUVRIR le cratère au retour et jouer l'allumage
 * de la pierre sous les yeux de l'élève.
 */

const PALETTE_PORTAILS = ['#b79bff', '#3ee6c4', '#6db4ff', '#ffc247', '#7dedb0', '#ff9bb8'];

const hexToRgba = (hex, a) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const reduceMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// ── Braises du cratère (canvas plein écran) + burst d'éruption ──────────
function BraisesCanvas({ burst }) {
    const ref = useRef(null);
    const burstRef = useRef(0);

    useEffect(() => {
        if (reduceMotion()) return undefined;
        const cv = ref.current;
        if (!cv) return undefined;
        const cx = cv.getContext('2d');
        let vivant = true;
        const fit = () => { cv.width = window.innerWidth; cv.height = window.innerHeight; };
        fit();
        window.addEventListener('resize', fit);

        const newP = (dessous) => ({
            x: Math.random() * cv.width,
            y: dessous ? cv.height + 10 : Math.random() * cv.height,
            r: 0.8 + Math.random() * 2.4,
            v: 0.35 + Math.random() * 1.1,
            drift: (Math.random() - 0.5) * 0.5,
            hue: 18 + Math.random() * 30,
            life: Math.random(),
        });
        const P = Array.from({ length: 70 }, () => newP(false));
        const B = []; // particules d'éruption

        const loop = () => {
            if (!vivant) return;
            cx.clearRect(0, 0, cv.width, cv.height);
            for (const p of P) {
                p.y -= p.v; p.x += p.drift + Math.sin(p.y * 0.01) * 0.3; p.life += 0.01;
                const fl = 0.5 + 0.5 * Math.sin(p.life * 7);
                cx.beginPath(); cx.arc(p.x, p.y, p.r, 0, 7);
                cx.fillStyle = `hsla(${p.hue},100%,${55 + fl * 20}%,${0.35 + fl * 0.45})`;
                cx.shadowColor = `hsla(${p.hue},100%,60%,.9)`; cx.shadowBlur = 8; cx.fill(); cx.shadowBlur = 0;
                if (p.y < -12) Object.assign(p, newP(true));
            }
            for (const b of B) {
                if (b.l <= 0) continue;
                b.x += b.vx; b.y += b.vy; b.vy += 0.18; b.l -= 0.012;
                cx.beginPath(); cx.arc(b.x, b.y, Math.max(b.r * b.l, 0.1), 0, 7);
                cx.fillStyle = `hsla(${b.hue},100%,62%,${Math.max(b.l, 0)})`;
                cx.shadowColor = `hsla(${b.hue},100%,60%,1)`; cx.shadowBlur = 12; cx.fill(); cx.shadowBlur = 0;
            }
            requestAnimationFrame(loop);
        };
        loop();

        // burst déclenché quand le compteur `burst` change (posé sur l'instance)
        cv._burst = () => {
            for (let i = 0; i < 160; i++) {
                const a = Math.random() * Math.PI * 2;
                const sp = 3 + Math.random() * 9;
                B.push({
                    x: cv.width / 2, y: cv.height / 2,
                    vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 3,
                    r: 2 + Math.random() * 4, hue: 15 + Math.random() * 40, l: 1,
                });
            }
        };

        return () => { vivant = false; window.removeEventListener('resize', fit); };
    }, []);

    useEffect(() => {
        if (burst > burstRef.current) {
            burstRef.current = burst;
            ref.current?._burst?.();
        }
    }, [burst]);

    return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />;
}

// ─────────────────────────────────────────────────────────────────────────
function VolcanRegion({ region, onStartDefi, onBack }) {
    const { profil } = useXp();
    const defisCredites = defisCreditesAffiches(profil);
    const quotaAtteint = defisCredites >= DEFIS_QUOTA_JOUR;
    const reposJour = defisEpuises(profil);
    const defisTotal = defisTotalAffiche(profil);

    // Retour de défi ? (compteur AVANT le défi, posé au lancement)
    const [retourCredits] = useState(() => {
        try {
            const v = sessionStorage.getItem('tanoVolcanCredits');
            sessionStorage.removeItem('tanoVolcanCredits');
            return v == null ? null : parseInt(v, 10);
        } catch { return null; }
    });

    // 'exterieur' → (dive) → 'cratere' ; retour de défi = cratère direct.
    const [scene, setScene] = useState(retourCredits != null ? 'cratere' : 'exterieur');
    const [plongee, setPlongee] = useState(false);
    const [flash, setFlash] = useState(false);
    const [defiOuvert, setDefiOuvert] = useState(null);
    const [pierreFraiche, setPierreFraiche] = useState(null); // index qui vient de s'allumer
    const [coreHappy, setCoreHappy] = useState(false);
    const [shake, setShake] = useState(false);
    const [eruption, setEruption] = useState(false);
    const [burst, setBurst] = useState(0);
    const retourTraite = useRef(false);

    const themes = useMemo(() => region.themes || [], [region.themes]);
    const portails = useMemo(() => themes.map((t, i) => {
        const n = themes.length;
        const ang = (-90 + (i * 360) / Math.max(n, 1)) * (Math.PI / 180);
        const lore = lorePortail(t.nom); // nom d'aventure + icône (ileLore)
        return {
            theme: t,
            jouable: attendusJouables(t).length > 0,
            couleur: PALETTE_PORTAILS[i % PALETTE_PORTAILS.length],
            ico: lore.ico,
            nomLore: lore.nom,
            left: 50 + 40 * Math.cos(ang),
            top: 50 + 40 * Math.sin(ang),
        };
    }), [themes]);

    // ── Retour de défi : la pierre s'allume sous les yeux de l'élève ──
    // (déclenché quand le profil refetché confirme le crédit)
    useEffect(() => {
        if (retourCredits == null || retourTraite.current) return;
        if (defisCredites > retourCredits) {
            retourTraite.current = true;
            const idx = Math.min(retourCredits, DEFIS_QUOTA_JOUR - 1);
            const t = setTimeout(() => {
                setPierreFraiche(idx);
                setCoreHappy(true);
                sonPierre();
                setTimeout(() => setCoreHappy(false), 800);
                // Passage à 5/5 → ÉRUPTION (une seule fois par jour)
                if (defisCredites >= DEFIS_QUOTA_JOUR) {
                    const auj = new Date().toDateString();
                    let dejaFete = false;
                    try {
                        dejaFete = localStorage.getItem('tanoEruptionJour') === auj;
                        if (!dejaFete) localStorage.setItem('tanoEruptionJour', auj);
                    } catch { /* no-op */ }
                    if (!dejaFete) {
                        setTimeout(() => {
                            sonEruption();
                            setShake(true);
                            setBurst((b) => b + 1);
                            setTimeout(() => setEruption(true), 1400);
                        }, 900);
                    }
                }
            }, 600);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [defisCredites, retourCredits]);

    const plonger = () => {
        sonPlongee();
        setPlongee(true);
        setTimeout(() => setFlash(true), 900);
        setTimeout(() => { setScene('cratere'); setFlash(false); }, 1400);
    };

    const lancerDefi = (theme) => {
        // onStartDefi renvoie true si le quiz démarre réellement — on ne pose
        // les flags de retour-cratère QU'APRÈS ce succès (posés avant, un
        // lancement avorté les laissait orphelins → catapultage fantôme dans
        // le cratère au prochain montage de l'île). L'écriture reste dans le
        // même tick React : elle précède toujours le démontage.
        const lance = onStartDefi?.(theme);
        if (!lance) return;
        try {
            sessionStorage.setItem('tanoIleRetourRegion', region.key);
            sessionStorage.setItem('tanoVolcanCredits', String(defisCredites));
        } catch { /* no-op */ }
    };

    return (
        <div className="volcan-exp fixed inset-0 z-[100] overflow-hidden" style={{ background: '#0b0812', fontFamily: 'Nunito, sans-serif', color: '#fff3e4' }}>

            {/* ═══ SCÈNE 1 : EXTÉRIEUR ═══ */}
            {scene === 'exterieur' && (
                <div
                    className={`vol-exterior absolute inset-0 flex items-center justify-center${plongee ? ' vol-dive' : ''}`}
                    style={{ background: 'radial-gradient(120% 90% at 50% 110%, #3a1020 0%, #1c0e2a 45%, #0b0812 100%)' }}
                >
                    {/* Étoiles */}
                    <div className="absolute inset-0" aria-hidden="true">
                        {Array.from({ length: 60 }, (_, i) => (
                            <span
                                key={i}
                                className="vol-star absolute w-0.5 h-0.5 rounded-full bg-white"
                                style={{
                                    left: `${(i * 61) % 100}%`,
                                    top: `${((i * 37) % 55)}%`,
                                    animationDelay: `${(i % 10) * 0.3}s`,
                                    transform: `scale(${0.6 + ((i * 13) % 10) / 7})`,
                                }}
                            />
                        ))}
                    </div>

                    {/* Le volcan */}
                    <svg className="absolute bottom-0 left-1/2 -translate-x-1/2" style={{ width: 'min(120vh, 100vw)' }} viewBox={VOLCAN_EXT.viewBox} preserveAspectRatio="xMidYMax meet" aria-hidden="true">
                        <defs>
                            <linearGradient id="volRock" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#2c1a34" /><stop offset="1" stopColor="#120a18" />
                            </linearGradient>
                            <linearGradient id="volLave" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#ffd873" /><stop offset=".5" stopColor="#ff6a1f" /><stop offset="1" stopColor="#c92a1e" />
                            </linearGradient>
                        </defs>
                        <path d={VOLCAN_EXT.montagne} fill="url(#volRock)" />
                        <path d={VOLCAN_EXT.bouche} fill="#0d0812" />
                        <path d={VOLCAN_EXT.coulee} fill="url(#volLave)" opacity=".9" />
                        <path d={VOLCAN_EXT.contrefortG} fill="#1a0f22" opacity=".8" />
                        <path d={VOLCAN_EXT.contrefortD} fill="#1a0f22" opacity=".8" />
                    </svg>
                    {/* Lueur du cratère qui respire */}
                    <div
                        className="vol-glow absolute left-1/2"
                        style={{ bottom: '38%', width: '22vmin', height: '10vmin', background: 'radial-gradient(ellipse at center, rgba(255,106,31,.85), rgba(255,106,31,0) 70%)', filter: 'blur(6px)' }}
                        aria-hidden="true"
                    />

                    {/* Retour archipel */}
                    <button onClick={onBack} aria-label="Retour à l'île" className="absolute top-5 left-5 p-2.5 rounded-xl text-white z-10" style={{ background: 'rgba(255,255,255,.1)' }}>
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    <div className="relative z-[5] text-center" style={{ transform: 'translateY(-9vh)' }}>
                        {/* filter (drop-shadow) et background-clip:text SÉPARÉS :
                            combinés sur le même élément, Chromium décale le dégradé
                            et coupe le haut des lettres (visible en grand écran). */}
                        <div className="vol-title" style={{ filter: 'drop-shadow(0 4px 24px rgba(255,106,31,.35))' }}>
                            <h1
                                style={{
                                    fontFamily: 'Fredoka', fontWeight: 700, fontSize: 'clamp(34px, 7vmin, 72px)',
                                    letterSpacing: '.02em', lineHeight: 1.25, padding: '0.08em 0.2em',
                                    background: 'linear-gradient(180deg,#ffe9b0,#ff8a3d 60%,#ff5030)',
                                    WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                                }}
                            >
                                {region.nom}
                            </h1>
                        </div>
                        <p className="vol-fade1 mt-2.5 font-bold" style={{ color: '#c9b6a8', fontSize: 'clamp(14px, 2.2vmin, 18px)' }}>
                            {themes.length} portail{themes.length > 1 ? 's' : ''}. Un noyau à réveiller. À toi de jouer.
                        </p>
                        <button
                            onClick={plonger}
                            className="vol-cta mt-8 px-9 py-4 rounded-full transition-transform hover:scale-105 active:scale-95"
                            style={{
                                fontFamily: 'Fredoka', fontWeight: 600, fontSize: 'clamp(16px, 2.6vmin, 22px)', color: '#2a0d05',
                                background: 'linear-gradient(180deg,#ffd873,#ff6a1f)',
                            }}
                        >
                            🌋 Plonger dans le cratère
                        </button>
                    </div>
                </div>
            )}

            {/* Flash de transition */}
            <div
                className="vol-flash fixed inset-0 pointer-events-none z-[60]"
                style={{ background: 'radial-gradient(circle at 50% 60%, #ffd873, #ff6a1f 45%, #b3231a 100%)', opacity: flash ? 1 : 0 }}
                aria-hidden="true"
            />

            {/* ═══ SCÈNE 2 : CRATÈRE ═══ */}
            {scene === 'cratere' && (
                <div
                    className={`absolute inset-0${shake ? ' vol-shake' : ''}`}
                    style={{
                        background: 'radial-gradient(90% 70% at 50% 118%, rgba(255,90,31,.32), transparent 60%), radial-gradient(130% 100% at 50% 50%, #241428 0%, #150c1c 55%, #090610 100%)',
                    }}
                >
                    <BraisesCanvas burst={burst} />

                    {/* Parois rocheuses */}
                    <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: '34vmin', opacity: 0.9 }} viewBox="0 0 200 200" aria-hidden="true">
                        <path d="M0,0 L200,0 L150,40 Q100,20 70,70 Q30,110 40,160 L0,200 Z" fill="#1a1122" />
                    </svg>
                    <svg className="absolute top-0 right-0 pointer-events-none" style={{ width: '34vmin', opacity: 0.9, transform: 'scaleX(-1)' }} viewBox="0 0 200 200" aria-hidden="true">
                        <path d="M0,0 L200,0 L150,40 Q100,20 70,70 Q30,110 40,160 L0,200 Z" fill="#1a1122" />
                    </svg>

                    {/* HUD : retour + énergie du volcan */}
                    <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-3 z-20">
                        <div className="flex items-center gap-2">
                            <button onClick={onBack} aria-label="Retour à l'île" className="p-2 rounded-xl text-white" style={{ background: 'rgba(255,255,255,.1)' }}>
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <span className="hidden sm:inline" style={{ fontFamily: 'Fredoka', fontWeight: 600, fontSize: 17, color: '#ffcf9a', textShadow: '0 2px 12px rgba(255,106,31,.5)' }}>
                                🌋 {region.nom}
                            </span>
                        </div>
                        <div className="flex items-center gap-2.5 px-4 py-2 rounded-full" style={{ background: 'rgba(11,8,18,.55)', border: '1px solid rgba(255,178,71,.35)', backdropFilter: 'blur(6px)' }}>
                            <span className="text-[12px] font-extrabold tracking-wide" style={{ color: '#ffcf9a' }}>🔥 ÉNERGIE</span>
                            {Array.from({ length: DEFIS_QUOTA_JOUR }, (_, i) => {
                                const lit = i < Math.min(defisCredites, DEFIS_QUOTA_JOUR);
                                return (
                                    <span
                                        key={i}
                                        className={`w-3.5 h-3.5 rounded-full transition-all${lit ? ' vol-dot-lit' : ''}`}
                                        style={lit
                                            ? { background: 'radial-gradient(circle at 35% 30%, #ffe9b0, #ff6a1f 70%)', border: '1px solid #ffb347', boxShadow: '0 0 12px rgba(255,140,50,.9)' }
                                            : { background: '#3a2b3f', border: '1px solid #5a4560' }}
                                    />
                                );
                            })}
                            <span style={{ fontFamily: 'Fredoka', fontWeight: 600, fontSize: 14, color: '#ffe9b0', minWidth: 30, textAlign: 'right' }}>
                                {Math.min(defisCredites, DEFIS_QUOTA_JOUR)}/{DEFIS_QUOTA_JOUR}
                            </span>
                        </div>
                    </div>

                    {/* Cœur du volcan + portails — l'anneau est borné par la HAUTEUR
                        du viewport (58vh) : sans ça, portails et étiquettes débordent
                        de ~30 % sur les écrans de portable. */}
                    <div className="absolute inset-0 grid place-items-center" style={{ paddingTop: 56, paddingBottom: 12 }}>
                        <div className="relative" style={{ width: 'min(86vmin, 58vh, 560px)', height: 'min(86vmin, 58vh, 560px)' }}>
                            {/* Noyau */}
                            <div
                                className={`vol-core absolute top-1/2 left-1/2 rounded-full z-[3]${coreHappy ? ' vol-core-happy' : ''}`}
                                style={{
                                    width: '23%', height: '23%', transform: 'translate(-50%,-50%)',
                                    background: 'radial-gradient(circle at 42% 36%, #ffe9b0 0%, #ffb347 30%, #ff6a1f 62%, #c92a1e 100%)',
                                    boxShadow: '0 0 40px 8px rgba(255,120,40,.55), 0 0 110px 30px rgba(255,80,30,.28), inset 0 -10px 30px rgba(140,20,10,.7)',
                                }}
                            >
                                <div className="vol-core-face absolute inset-0 grid place-items-center" style={{ fontSize: 'min(6vmin, 40px)', filter: 'drop-shadow(0 2px 4px rgba(120,20,0,.6))' }}>
                                    {reposJour ? '💤' : quotaAtteint ? '😎' : '🔥'}
                                </div>
                            </div>

                            {/* Les 5 pierres d'énergie */}
                            {Array.from({ length: DEFIS_QUOTA_JOUR }, (_, i) => {
                                const lit = i < Math.min(defisCredites, DEFIS_QUOTA_JOUR);
                                return (
                                    <div
                                        key={i}
                                        className={`absolute top-1/2 left-1/2 z-[4]${lit && pierreFraiche === i ? ' vol-stone-ignite' : ''}`}
                                        style={{
                                            '--rot': `${i * (360 / DEFIS_QUOTA_JOUR)}deg`,
                                            width: '8%', height: '8%',
                                            borderRadius: '38% 62% 55% 45% / 50% 42% 58% 50%',
                                            transform: 'translate(-50%,-50%) rotate(var(--rot)) translateY(-215%)',
                                            background: lit
                                                ? 'radial-gradient(circle at 40% 32%, #ffe9b0, #ff8a3d 55%, #e6392e)'
                                                : 'linear-gradient(150deg, #3c2c44, #221827)',
                                            border: lit ? '1px solid #ffd873' : '1px solid #57405f',
                                            boxShadow: lit ? '0 0 18px rgba(255,150,60,1), 0 0 40px rgba(255,90,31,.6)' : 'none',
                                            transition: 'all .5s',
                                        }}
                                        aria-hidden="true"
                                    />
                                );
                            })}

                            {/* Portails = thèmes réels */}
                            {portails.map((p) => {
                                const actif = p.jouable && !reposJour;
                                return (
                                    <div
                                        key={p.theme.id || p.theme.nom}
                                        className="vol-portal absolute flex flex-col items-center gap-1.5 text-center z-[6]"
                                        style={{ top: `${p.top}%`, left: `${p.left}%`, transform: 'translate(-50%,-50%)', width: 'clamp(110px, 19%, 160px)' }}
                                    >
                                        <button
                                            type="button"
                                            disabled={!actif}
                                            onClick={actif ? () => { sonClicVolcan(); setDefiOuvert(p.theme); } : undefined}
                                            aria-label={p.theme.nom}
                                            title={p.jouable ? (reposJour ? 'Repos ! Reviens demain 💤' : p.theme.nom) : `${p.theme.nom} — Bientôt`}
                                            className="relative grid place-items-center overflow-hidden transition-transform hover:scale-110 active:scale-95"
                                            style={{
                                                width: 'clamp(66px, 11vmin, 92px)', height: 'clamp(80px, 13.5vmin, 112px)',
                                                borderRadius: '50% 50% 12% 12% / 62% 62% 8% 8%',
                                                cursor: actif ? 'pointer' : 'default',
                                                background: actif
                                                    ? `radial-gradient(ellipse at 50% 34%, ${hexToRgba(p.couleur, 0.4)}, #17101f 75%)`
                                                    : 'radial-gradient(ellipse at 50% 34%, rgba(90,69,96,.35), #140d1a 75%)',
                                                border: `3px solid ${actif ? p.couleur : '#4a3852'}`,
                                                boxShadow: actif
                                                    ? `0 0 16px ${hexToRgba(p.couleur, 0.55)}, inset 0 0 26px ${hexToRgba(p.couleur, 0.4)}`
                                                    : 'inset 0 0 20px rgba(0,0,0,.5)',
                                                opacity: actif ? 1 : 0.65,
                                            }}
                                        >
                                            {actif && (
                                                <span
                                                    className="vol-swirl absolute rounded-[inherit]"
                                                    style={{
                                                        inset: '8%', opacity: 0.5, filter: 'blur(6px)',
                                                        background: `conic-gradient(from 0deg, transparent, ${p.couleur}, transparent 30%, ${p.couleur} 55%, transparent 80%)`,
                                                    }}
                                                    aria-hidden="true"
                                                />
                                            )}
                                            <span className="vol-float relative z-[2]" style={{ fontSize: 'clamp(28px, 4.6vmin, 38px)', filter: actif ? `drop-shadow(0 0 8px ${p.couleur})` : 'grayscale(.7)' }}>
                                                {p.jouable ? (reposJour ? '💤' : p.ico) : '🌫️'}
                                            </span>
                                        </button>
                                        <div
                                            className="leading-tight"
                                            style={{ fontFamily: 'Fredoka', fontWeight: 600, fontSize: 'clamp(12px, 1.9vmin, 15px)', color: actif ? p.couleur : '#8a7a92', textShadow: actif ? `0 0 14px ${hexToRgba(p.couleur, 0.65)}` : 'none' }}
                                        >
                                            {p.nomLore}
                                        </div>
                                        <div className="leading-tight font-bold" style={{ fontSize: 'clamp(10px, 1.4vmin, 12px)', color: '#c9b6a8', opacity: 0.85 }}>
                                            {p.jouable ? (reposJour ? 'Repos !' : quotaAtteint ? 'Entraînement libre' : 'Tape pour un défi !') : 'Bientôt ✨'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Fiche défi (moteur réel) */}
            {defiOuvert && (
                <FicheDefi
                    theme={defiOuvert}
                    matiereLabel="Maths"
                    quotaAtteint={quotaAtteint}
                    reposJour={reposJour}
                    defisCredites={defisCredites}
                    defisTotal={defisTotal}
                    onLancer={() => { lancerDefi(defiOuvert); setDefiOuvert(null); }}
                    onFermer={() => setDefiOuvert(null)}
                />
            )}

            {/* ═══ ÉRUPTION (5/5, une fois par jour) ═══ */}
            {eruption && (
                <div
                    className="fixed inset-0 z-[110] grid place-items-center"
                    style={{ background: 'radial-gradient(90% 90% at 50% 100%, rgba(255,90,31,.35), rgba(6,4,10,.88) 60%)' }}
                >
                    <div className="text-center px-6">
                        <div className="vol-float" style={{ fontSize: 88, filter: 'drop-shadow(0 0 30px rgba(255,150,60,.9))' }}>🏆</div>
                        <h2
                            style={{
                                fontFamily: 'Fredoka', fontWeight: 700, fontSize: 'clamp(28px, 5.5vmin, 44px)', marginTop: 8,
                                background: 'linear-gradient(180deg,#fff6da,#ffb347 70%)',
                                WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                            }}
                        >
                            ÉRUPTION !
                        </h2>
                        <p className="mt-2.5 font-bold" style={{ color: '#c9b6a8', fontSize: 16 }}>
                            Le volcan est à pleine puissance : tes 5 défis du jour sont gagnés !<br />
                            Tu es un vrai <strong style={{ color: '#ffd873' }}>Maître du Volcan</strong> 🌋
                        </p>
                        <button
                            onClick={() => { sonClicVolcan(); setEruption(false); setShake(false); }}
                            className="mt-6 px-8 py-3.5 rounded-full transition-transform hover:scale-105 active:scale-95"
                            style={{ fontFamily: 'Fredoka', fontWeight: 600, fontSize: 18, color: '#2a0d05', background: 'linear-gradient(180deg,#ffd873,#ff6a1f)', boxShadow: '0 8px 30px rgba(255,90,31,.5)' }}
                        >
                            Continuer à explorer
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default VolcanRegion;
